import React, { useContext, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { webAnalyticsContext } from '@satreg/analytics';
import { useApiKey } from '@satreg/api';
import { authContext } from '@satreg/auth';
import { useLocallyStoredState } from '@satreg/react-utils';
import { Spinner } from '@satreg/spinner';
import { fallbackRoute } from '../components/registrationFlow/stepUtils';
import { AwsRumType } from './App';

const NO_OF_RETRIES = 20;
const INTERVAL = 250;

// TODO: Get this working in widgets, for some reason this does not work when using from widgets.
export const lazyReactNaiveRetry: typeof React.lazy = (importer) => {
    const retryImport = async () => {
        try {
            return await importer();
        } catch (error: any) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < NO_OF_RETRIES; i++) {
                // eslint-disable-next-line no-await-in-loop,no-promise-executor-return
                await new Promise((resolve) => setTimeout(resolve, INTERVAL));
                const url = new URL(error.message.replace('Failed to fetch dynamically imported module: ', '').trim());
                // add a timestamp to the url to force a reload the module (and not use the cached version - cache busting)
                url.searchParams.set('t', `${+new Date()}`);

                try {
                    // eslint-disable-next-line no-await-in-loop
                    return await import(url.href);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log(e, 'retrying import');
                }
            }
            throw error;
        }
    };
    return React.lazy(retryImport);
};

const RegistrationApp = lazyReactNaiveRetry(() => import('./registration'));

const ALLOWED_DOMAIN = ['.collegeboard.org'];

const AppSelector: React.FC<AwsRumType> = ({ awsRum }) => {
    const { userInfo } = useContext(authContext);
    const { fireUserDataEvent } = useContext(webAnalyticsContext);
    const { apiKeys } = useApiKey();

    // Ecom has no referrer, so instead we hold a state as a pseudo-referrer since if we made it this far we know the use authenticated and has attempted to make a payment.
    const [localStoredPayment] = useLocallyStoredState('is-on-payment', false);

    /**
     * A useEffect to check if we came from an appropriate site, if not we redirect them to mysat, else we let them continue
     */
    useEffect(() => {
        const { referrer } = document;
        const retrievedDomain = ALLOWED_DOMAIN.find((domain: string) => referrer.includes(domain));

        // If we have no referrer of any kind we wipe the data out and redirect to mysat, this means we are coming from a non authorized URL (direct URL entry)
        if (!retrievedDomain && !localStoredPayment) {
            sessionStorage.clear();
            window.location.replace(import.meta.env.VITE_MY_SAT_URL || '');
        }
    }, []);

    /**
     * When userInfo is loaded - report it to Web Analytics
     */
    useEffect(() => {
        if (userInfo) {
            fireUserDataEvent({
                cbWeb: {
                    type: 'ECL', // Currently Set To ECL
                    id: userInfo.id, // Currently Logged In CB Web User Account ID
                    eclId: userInfo.id, // Currently Logged In CB Web User ECL ID (same as Id)
                    gradYear: userInfo.cohort, // Currently Logged In CB Web User Grad Year
                    orgId: userInfo.orgId, // Currently Logged In CB Web User Org ID (6-digits)
                    gender: userInfo.gender, // Currently Logged In CB Web User Gender
                },
            });
        }
    }, [userInfo]);

    if (!apiKeys) {
        return <Spinner />;
    }

    return (
        <Routes>
            <Route path="/register/*" element={<RegistrationApp awsRum={awsRum} />} />
            {/* Essentially the 404 route. Use the default fallback behavior. */}
            <Route path="*" element={<Navigate to={fallbackRoute} replace />} />
        </Routes>
    );
};

export default AppSelector;
